import React from 'react';
import { FadeInSection, Divider } from './ui';
import { AlertTriangle, XCircle, Clock, DollarSign } from 'lucide-react';
export const Problem = () => {
  return <div className="bg-brand-cream" data-id="element-135">
      {/* SECCIÓN 1: GANCHO & AGITACIÓN */}
      <section className="py-16 md:py-24 px-4 max-w-4xl mx-auto" data-id="element-136">
        <FadeInSection data-id="element-137">
          <h2 className="text-3xl md:text-4xl font-playfair font-bold text-center mb-10 text-brand-brown" data-id="element-138">
            ¿Recuerdas cómo era tu cabello antes?
          </h2>

          <div className="space-y-6 text-lg md:text-xl text-brand-brown/80 leading-relaxed" data-id="element-139">
            <p data-id="element-140">
              Antes del tinte. Antes de la decoloración. Antes del alisado o la
              permanente.
            </p>

            <p data-id="element-141">
              Recuerdas cuando podías pasarte los dedos por el cabello y sentir
              algo suave. Cuando se secaba al aire y quedaba bien. Cuando no
              tenías miedo de lavarlo porque sabías que no se iba a romper.
            </p>

            <p data-id="element-142">
              Ese cabello todavía existe dentro del daño. Solo está atrapado
              bajo capas de decisiones mal ejecutadas, productos en el orden
              equivocado, y consejos que sonaban bien pero no funcionaron.
            </p>

            <p data-id="element-143">
              Y mientras tanto… sigues esperando. Escondiendo el cabello en una
              cola. Evitando que alguien lo toque. Poniendo filtros en las fotos
              para que no se note el estado real. Cancelando planes porque "el
              cabello no te da".
            </p>

            <div className="bg-brand-creamDark/50 p-8 rounded-2xl border-l-4 border-brand-copper my-10" data-id="element-144">
              <p className="font-playfair font-bold text-2xl md:text-3xl text-brand-brown mb-4" data-id="element-145">
                Eso no es vanidad. Eso es identidad.
              </p>
              <p data-id="element-146">
                El cabello para una mujer no es decoración. Es confianza. Es
                presencia. Es poder caminar a una reunión, a una cita, a una
                entrevista — y sentirte entera.
              </p>
              <p className="mt-4 font-medium" data-id="element-147">
                Cuando el cabello falla, algo más también falla.
              </p>
              <p className="mt-2 text-brand-copper font-bold" data-id="element-148">Y tú lo sabes.</p>
            </div>
          </div>
        </FadeInSection>
      </section>

      <Divider data-id="element-149" />

      {/* SECCIÓN 2: AMPLIFICACIÓN DEL DOLOR */}
      <section className="py-16 px-4 max-w-4xl mx-auto" data-id="element-150">
        <FadeInSection data-id="element-151">
          <h2 className="text-3xl md:text-4xl font-playfair font-bold text-center mb-10" data-id="element-152">
            Lo más cruel del cabello dañado por químicos no es el aspecto.
          </h2>
          <h3 className="text-2xl text-center text-brand-copper font-playfair italic mb-10" data-id="element-153">
            Es el ciclo de desesperanza.
          </h3>

          <div className="space-y-6 text-lg text-brand-brown/80 leading-relaxed" data-id="element-154">
            <p data-id="element-155">
              Compras una mascarilla con el frasco más bonito del estante. La
              aplicas. Esperas. Enjuagas. Y por dos días, parece que algo
              mejoró. Pero al tercer lavado, el cabello vuelve a sentirse raro.
              Vuelve la textura chiclosa. Vuelven los quiebres.
            </p>

            <p data-id="element-156">
              Entonces pruebas otra cosa. Un aceite. Una proteína de farmacia.
              Un remedio casero que viste en TikTok. Un tratamiento caro que
              prometía milagros.
            </p>

            <p className="font-medium text-brand-brown" data-id="element-157">
              Y el ciclo se repite.
            </p>

            <div className="bg-white shadow-xl shadow-brand-brown/5 p-8 md:p-10 rounded-2xl border border-brand-copper/20 relative my-12" data-id="element-158">
              <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-brand-copper text-white px-6 py-2 rounded-full font-bold text-sm tracking-wider uppercase" data-id="element-159">
                La Verdad Oculta
              </div>
              <p className="text-xl md:text-2xl font-playfair text-center leading-relaxed" data-id="element-160">
                Lo que nadie te dice es esto:{' '}
                <strong className="text-brand-red" data-id="element-161">
                  no es que los productos no funcionen. Es que los estás usando
                  sin el sistema correcto.
                </strong>{' '}
                Sin el orden correcto. Sin entender qué necesita tu cabello en
                cada etapa específica de su daño.
              </p>
            </div>

            <p data-id="element-162">
              Y mientras sigues probando al azar, la fibra capilar sigue
              debilitándose. Cada lavado, cada peinada, cada intento de
              "arreglarlo" sin el protocolo adecuado lo acerca más al punto de
              no retorno.
            </p>

            <p className="text-2xl font-playfair font-bold text-center text-brand-brown mt-8" data-id="element-163">
              Pero ese punto todavía no llegó.
            </p>
          </div>
        </FadeInSection>
      </section>

      {/* SECCIÓN 3: RESULTADO SOÑADO */}
      <section className="py-20 px-4 bg-brand-brown text-brand-cream relative overflow-hidden" data-id="element-164">
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-brand-copper via-transparent to-transparent" data-id="element-165"></div>
        <div className="max-w-4xl mx-auto relative z-10" data-id="element-166">
          <FadeInSection data-id="element-167">
            <h2 className="text-4xl md:text-5xl font-playfair font-bold text-center mb-12 text-brand-copperLight" data-id="element-168">
              Imagina despertar el día 7.
            </h2>

            <div className="space-y-8 text-lg md:text-xl leading-relaxed text-brand-cream/90 text-center max-w-3xl mx-auto" data-id="element-169">
              <p data-id="element-170">
                Te lavas el cabello. Lo enjuagas. Y cuando pasas los dedos por
                él, no sientes goma. Sientes cabello. Tu cabello.
              </p>

              <p data-id="element-171">
                Te lo peinas sin miedo. Te lo dejas secando al aire y no parece
                una escoba. Te miras al espejo y algo regresa — no solo el
                cabello.{' '}
                <strong className="text-white text-2xl font-playfair block mt-2" data-id="element-172">
                  Tú regresas.
                </strong>
              </p>

              <p className="italic text-brand-copperLight" data-id="element-173">
                Esa mujer que salía sin pensar dos veces si el cabello "se veía
                bien". Esa que se sacaba fotos sin filtro capilar. Esa que
                recibía el comentario: "¿Qué le hiciste al cabello? Se ve
                increíble."
              </p>

              <div className="pt-8 space-y-4 font-playfair text-2xl" data-id="element-174">
                <p className="flex items-center justify-center gap-3" data-id="element-175">
                  <span className="text-brand-copper" data-id="element-176">✦</span> No tienes que ir
                  a un salón para volver a ser ella.
                </p>
                <p className="flex items-center justify-center gap-3" data-id="element-177">
                  <span className="text-brand-copper" data-id="element-178">✦</span> No tienes que
                  gastar lo que no tienes.
                </p>
                <p className="flex items-center justify-center gap-3" data-id="element-179">
                  <span className="text-brand-copper" data-id="element-180">✦</span> No tienes que
                  cortarte lo que no quieres perder.
                </p>
              </div>

              <p className="text-2xl font-bold text-white pt-6" data-id="element-181">
                Solo necesitas el sistema correcto. El orden correcto. Y 7 días.
              </p>
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* SECCIÓN 4: POR QUÉ LO ANTERIOR NO FUNCIONÓ */}
      <section className="py-24 px-4 max-w-6xl mx-auto" data-id="element-182">
        <FadeInSection data-id="element-183">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-184">
            <h2 className="text-3xl md:text-4xl font-playfair font-bold text-brand-brown mb-6" data-id="element-185">
              Antes de presentarte la solución, necesitas entender por qué todo
              lo que intentaste antes no funcionó
            </h2>
            <p className="text-xl text-brand-copper font-bold" data-id="element-186">
              — porque no fue tu culpa.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-187">
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-brand-creamDark hover:shadow-md transition-shadow" data-id="element-188">
              <div className="w-12 h-12 bg-red-50 text-brand-red rounded-full flex items-center justify-center mb-6" data-id="element-189">
                <AlertTriangle size={24} data-id="element-190" />
              </div>
              <h3 className="text-xl font-bold font-playfair mb-3" data-id="element-191">
                Mascarillas sin diagnóstico
              </h3>
              <p className="text-brand-brown/70" data-id="element-192">
                Aplicar hidratación en un cabello que primero necesita proteína
                es como echarle agua a una esponja rota. Se absorbe, pero no
                reconstruye nada.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-sm border border-brand-creamDark hover:shadow-md transition-shadow" data-id="element-193">
              <div className="w-12 h-12 bg-red-50 text-brand-red rounded-full flex items-center justify-center mb-6" data-id="element-194">
                <XCircle size={24} data-id="element-195" />
              </div>
              <h3 className="text-xl font-bold font-playfair mb-3" data-id="element-196">
                Tutoriales sin estructura
              </h3>
              <p className="text-brand-brown/70" data-id="element-197">
                Un video de YouTube no sabe cuál es tu nivel de daño. No puede
                decirte si tu cabello está en Nivel 2 o Nivel 5. Sin
                diagnóstico, cualquier tratamiento es un disparo al aire.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-sm border border-brand-creamDark hover:shadow-md transition-shadow" data-id="element-198">
              <div className="w-12 h-12 bg-red-50 text-brand-red rounded-full flex items-center justify-center mb-6" data-id="element-199">
                <Clock size={24} data-id="element-200" />
              </div>
              <h3 className="text-xl font-bold font-playfair mb-3" data-id="element-201">
                El salón como solución temporal
              </h3>
              <p className="text-brand-brown/70" data-id="element-202">
                Una sesión de keratina o un tratamiento en salón puede verse
                bien por semanas — pero sin una rutina de mantenimiento correcta
                en casa, el daño vuelve. Siempre vuelve.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-sm border border-brand-creamDark hover:shadow-md transition-shadow" data-id="element-203">
              <div className="w-12 h-12 bg-red-50 text-brand-red rounded-full flex items-center justify-center mb-6" data-id="element-204">
                <DollarSign size={24} data-id="element-205" />
              </div>
              <h3 className="text-xl font-bold font-playfair mb-3" data-id="element-206">
                Productos premium sin protocolo
              </h3>
              <p className="text-brand-brown/70" data-id="element-207">
                Gastar más no garantiza resultados. Un producto de $80 aplicado
                en el orden equivocado hace menos que uno de $8 aplicado
                correctamente.
              </p>
            </div>
          </div>

          <div className="mt-16 text-center" data-id="element-208">
            <p className="text-2xl md:text-3xl font-playfair font-bold text-brand-brown bg-brand-copper/10 inline-block px-8 py-4 rounded-xl" data-id="element-209">
              El problema nunca fue tu cabello. Fue la ausencia de un sistema.
            </p>
          </div>
        </FadeInSection>
      </section>
    </div>;
};